// ═══════════════════════════════════════════════════════════════════════════
// useDirectorioEmpresa — las personas que pueden recibir una tarea de UNA empresa
// ═══════════════════════════════════════════════════════════════════════════
//
// Report du3xh8vn8Gmd8fmmf5aB: varios selectores de personas se llenaban con
// `getDocs(collection(db, COLLECTIONS.USUARIOS))` — query de LISTA sobre la
// raíz de `usuarios`, sin `where` y sin `limit`. Dos consecuencias medidas:
//   1. Cuesta plata en proporción a los usuarios de TODA la plataforma, en
//      pantallas de uso diario (`constraints.md §7.B`).
//   2. El único `allow read` de `usuarios` satisfacible sin `resource.data` es
//      `isConsultor()`, así que para cualquier otro perfil la query se rechaza
//      ENTERA y el `<select>` queda vacío, en silencio. Costo sin resultado.
//
// POR QUÉ ESTE HOOK EXISTE, y no un `where` copiado en cada pantalla. El
// criterio de "quién puede recibir una tarea de esta empresa" ya está decidido
// y ya está implementado una vez, en el callable `usuariosParaDelegar`
// (`functions/usuariosParaDelegar.js`) — con el gate de tenant, la allowlist
// campo por campo y el criterio textual de Mariano (22-ago-2026): *"a todos
// los usuarios que tenés acceso por tu rol y la empresa en la que está"*.
// Este hook es el ÚNICO camino del front a ese criterio.
//
// La forma es deliberada: la lección `camino-manual-olvidado` de
// `.claude/agents/forense-lecciones.md` (recurrencia 4, vigente) dice que
// cuando el mismo dato se carga por varios caminos, el guard hay que ponerlo
// en un helper único que todos llamen — si no, el camino que queda afuera es
// el próximo incidente. Por eso se exporta también la variante imperativa
// (`cargarDirectorioEmpresa`), para los flujos que no pueden usar un hook
// (abrir un modal, resolver varias empresas a la vez): el cache y el criterio
// son los mismos, no hay una segunda fuente.
//
// El cache guarda SOLO éxitos. Un fallo transitorio no se memoiza: cachear el
// error dejaría el selector vacío para siempre en esa sesión, que es
// exactamente el síntoma que este report vino a cerrar.
// ═══════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useRef, useMemo } from 'react'
import { getUsuariosParaDelegar } from '@core/lib/usuarios'

// ── Cache de sesión ──────────────────────────────────────────────────────
const _cache = new Map()      // empresaId → Array<{id, nombre, email}>
const _inflight = new Map()   // empresaId → Promise<{usuarios, error}>

/** Vaciá el cache al logout (mismo motivo que `limpiarCachesNombres`). */
export function limpiarCacheDirectorioEmpresa() {
  _cache.clear()
  _inflight.clear()
}

/**
 * Variante imperativa — para flujos que no son un hook (abrir un modal,
 * resolver varias empresas). Comparte cache y criterio con el hook.
 *
 * @param {string} empresaId
 * @returns {Promise<{usuarios: Array<{id,nombre,email}>, error: string|null}>}
 */
export async function cargarDirectorioEmpresa(empresaId) {
  if (!empresaId) return { usuarios: [], error: null }
  if (_cache.has(empresaId)) return { usuarios: _cache.get(empresaId), error: null }
  if (_inflight.has(empresaId)) return _inflight.get(empresaId)

  const promesa = (async () => {
    const { usuarios, error } = await getUsuariosParaDelegar(empresaId)
    // Solo los éxitos entran al cache (ver cabecera).
    if (!error) _cache.set(empresaId, usuarios)
    return { usuarios, error }
  })().finally(() => { _inflight.delete(empresaId) })

  _inflight.set(empresaId, promesa)
  return promesa
}

/**
 * PURA — el directorio con el que pide adelante, para las acciones que admiten
 * asignarse a uno mismo (crear una tarea, aprobar una señal, «me llevo yo estas
 * ocho»). El callable lo excluye SIEMPRE porque nació para DELEGAR, y uno no se
 * delega a sí mismo: la exclusión es de la acción, no del directorio, así que la
 * decide cada pantalla (report wSXD7BmgVGQENaTGQGTW). Una regla y un lugar: las
 * pantallas que la necesitan la llaman, no la copian.
 *
 * No agrega nada mientras el directorio no está confirmado (cargando, error,
 * truncado): una lista que dice sólo «yo» se lee como «no hay nadie más».
 * Confirmado, es seguro por construcción: si el callable resolvió sin error,
 * el que pide pasó `requireEmpresaAccess` en esa empresa.
 *
 * @param {Array<{id,nombre,email}>} usuarios el directorio tal como vino
 * @param {{uid?: string, nombre?: string, email?: string}} yo el que pide
 * @param {{confirmado: boolean}} estado
 */
export function conElQuePide(usuarios, yo, { confirmado }) {
  if (!confirmado || !yo?.uid) return usuarios
  if (usuarios.some(u => u.id === yo.uid)) return usuarios
  return [{ id: yo.uid, nombre: `${yo.nombre || 'Yo'} (yo)`, email: yo.email || '' }, ...usuarios]
}

/**
 * Hook: el directorio de personas de una empresa, para poblar un selector.
 *
 * Reacciona al cambio de `empresaId` (un modal que tiene su propio selector de
 * empresa cambia de directorio al cambiar de empresa). Con `empresaId` vacío
 * devuelve lista vacía sin pedir nada — no hay "todas las empresas".
 *
 * @param {string|null} empresaId
 * @returns {{usuarios: Array<{id,nombre,email}>, cargando: boolean, error: string|null}}
 */
export function useDirectorioEmpresa(empresaId) {
  const [usuarios, setUsuarios] = useState(() => _cache.get(empresaId) || [])
  const [cargando, setCargando] = useState(false)
  const [error, setError] = useState(null)
  const montado = useRef(true)

  useEffect(() => {
    montado.current = true
    return () => { montado.current = false }
  }, [])

  useEffect(() => {
    if (!empresaId) { setUsuarios([]); setError(null); setCargando(false); return }

    // Cache hit: sin pedido y sin parpadeo de "cargando".
    if (_cache.has(empresaId)) {
      setUsuarios(_cache.get(empresaId)); setError(null); setCargando(false)
      return
    }

    let cancelado = false
    setCargando(true)
    setError(null)
    cargarDirectorioEmpresa(empresaId).then(({ usuarios: lista, error: err }) => {
      // `cancelado` cubre el cambio de empresaId; `montado` el desmontaje.
      // Sin el primero, la respuesta de la empresa vieja puede pisar a la nueva.
      if (cancelado || !montado.current) return
      setUsuarios(lista)
      setError(err)
      setCargando(false)
    })
    return () => { cancelado = true }
  }, [empresaId])

  return { usuarios, cargando, error }
}

// ═══════════════════════════════════════════════════════════════════════════
// useDirectorioEmpresas (PLURAL) — quién puede recibir un LOTE cross-empresa
// ═══════════════════════════════════════════════════════════════════════════
//
// Report du3xh8vn8Gmd8fmmf5aB, tajada 2. La barra de acciones masivas de
// `AsignacionesPage` reasigna N tareas de una vez, y esas N tareas pueden ser
// de empresas DISTINTAS (la vista global de un consultor las mezcla). El
// invariante que sostiene el criterio de Mariano —"el destino de una tarea
// tiene acceso a la empresa DE ESA tarea"— sólo se cumple conjuntivamente
// sobre un lote: el destino tiene que ser válido para CADA tarea. Eso es la
// INTERSECCIÓN de los directorios, y no es criterio nuevo: es el único
// criterio compuesto de la única forma que no rompe tenancy. La unión sí
// sería criterio nuevo, y además fail-open — ofrecería a alguien sin acceso a
// una de las empresas del lote.
//
// TRES COSAS QUE ESTE HOOK HACE Y UN `Promise.all` suelto NO haría:
//
// 1. FALLA CERRADO ANTE UN ERROR PARCIAL. `cargarDirectorioEmpresa` devuelve
//    `{usuarios: [], error}` cuando no pudo resolver, y `[]` ANIQUILA una
//    intersección. Sin este guard, una empresa que falla produce una lista
//    vacía indistinguible de "no hay nadie en común" — que es, textualmente,
//    el síntoma que este report vino a cerrar, reintroducido con otra causa.
//    Por eso ante CUALQUIER error no se publica la intersección parcial: se
//    devuelve `error` y la lista vacía, y el consumidor tiene que decirlo.
//
// 2. DECLARA LA COTA DEL FAN-OUT, y la hace viajar. La cota de usuarios POR
//    empresa ya la declara el callable (`CAP_USUARIOS_EMPRESA`), pero la
//    intersección introduce un multiplicador que hasta acá no declaraba
//    nadie: M empresas distintas = M invocaciones. Se declara `CAP_EMPRESAS`,
//    se falla cerrado al pasarlo y el motivo llega al consumidor en
//    `excedeCota` — un tope mudo es un número que miente (misma lección que
//    el «500+» del chip de EmpresasPage, tajada 1).
//
// 3. MEMOIZA POR EL CONJUNTO DE EMPRESAS, no por la selección. La selección
//    cambia con cada tilde; el conjunto de empresas, no. Sin esto, tildar la
//    quinta tarea de la misma empresa vuelve a disparar el fan-out entero.
//    El cache de `cargarDirectorioEmpresa` (sólo éxitos) hace que el costo
//    incremental real sea una invocación por empresa NUEVA, no por click.
//
// El criterio, el cache y el flush de logout son los MISMOS que la variante
// singular: esto compone `cargarDirectorioEmpresa`, no la reimplementa. No
// hay una segunda fuente.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Cota del fan-out cross-empresa: cuántas empresas distintas puede abarcar un
 * lote antes de que el selector se plante. Se declara acá —y no en la página—
 * porque es una propiedad del acceso al directorio, no de una pantalla.
 */
export const CAP_EMPRESAS = 10

/** Clave estable de un conjunto de empresas: ordenada y sin repetidos. */
function _claveEmpresas(empresaIds) {
  return [...new Set((empresaIds || []).filter(Boolean).map(String))].sort().join(',')
}

/** Intersección por `id` de varias listas de usuarios. Conserva el orden de la primera. */
function _interseccion(listas) {
  const [primera = [], ...resto] = listas
  const comunes = resto.map(l => new Set(l.map(u => u.id)))
  return primera.filter(u => comunes.every(s => s.has(u.id)))
}

/**
 * Hook: las personas que pueden recibir CUALQUIERA de las tareas de un lote.
 *
 * @param {string[]} empresaIds — las empresas del lote (se deduplican acá).
 * @returns {{
 *   usuarios: Array<{id,nombre,email}>,
 *   cargando: boolean,
 *   error: string|null,
 *   empresas: number,
 *   excedeCota: boolean
 * }} `usuarios` es la intersección, y es `[]` en TODOS los casos en que no se
 *   pudo calcular entera — `error` y `excedeCota` son los que distinguen "no
 *   hay nadie en común" de "no pude averiguarlo".
 */
export function useDirectorioEmpresas(empresaIds) {
  const clave = _claveEmpresas(empresaIds)
  const [estado, setEstado] = useState({ usuarios: [], cargando: false, error: null })
  const ids = useMemo(() => (clave ? clave.split(',') : []), [clave])
  const excedeCota = ids.length > CAP_EMPRESAS

  useEffect(() => {
    if (ids.length === 0 || ids.length > CAP_EMPRESAS) {
      setEstado({ usuarios: [], cargando: false, error: null })
      return
    }

    let cancelado = false
    setEstado({ usuarios: [], cargando: true, error: null })
    Promise.all(ids.map(id => cargarDirectorioEmpresa(id))).then(res => {
      // `cancelado` cubre las DOS salidas —cambio de conjunto y desmontaje—
      // porque el cleanup de un efecto corre también al desmontar.
      if (cancelado) return
      const fallidas = res.filter(r => r.error)
      if (fallidas.length > 0) {
        // Fail-closed: NO se publica la intersección parcial (ver cabecera, 1).
        setEstado({
          usuarios: [],
          cargando: false,
          error: `No se pudo resolver el directorio de ${fallidas.length} de ${ids.length} empresa${ids.length === 1 ? '' : 's'}`,
        })
        return
      }
      setEstado({ usuarios: _interseccion(res.map(r => r.usuarios)), cargando: false, error: null })
    })
    return () => { cancelado = true }
  }, [ids])

  return { ...estado, empresas: ids.length, excedeCota }
}
